import React, { useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { Box, Container, Typography, Stack, Paper } from "@mui/material";
import { Home, Compass, HelpCircle, ArrowLeft } from "lucide-react";
import NavBar from "../../Components/NavBar";
import CommonButton from "../../Components/ui/inputs/CommonButton";
import { colors, customShadows, borderRadiusPx } from "../../../theme";
import { Path } from "../../../static/Constants";

/**
 * NotFound Component (404 Page)
 * Displays a responsive, theme-consistent 404 error page for unlisted routes,
 * with navigation options back to Home, Games, and Help.
 */
export default function NotFound() {
  const history = useHistory();

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "404 - Page Not Found | AutoBET";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <>
      <NavBar />
      <Box
        component="main"
        sx={{
          minHeight: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          pt: { xs: "100px", sm: "120px" },
          pb: 8,
          background: colors?.background?.subtle || "#070b28",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle decorative glowing background blur circles */}
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            left: "15%",
            width: { xs: 200, md: 350 },
            height: { xs: 200, md: 350 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0) 70%)",
            pointerEvents: "none",
            filter: "blur(40px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            right: "15%",
            width: { xs: 220, md: 380 },
            height: { xs: 220, md: 380 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0) 70%)",
            pointerEvents: "none",
            filter: "blur(40px)",
          }}
        />

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              textAlign: "center",
              borderRadius: borderRadiusPx?.xl || 4,
              border: `1px solid ${
                colors?.neutral?.border || "rgba(255, 255, 255, 0.08)"
              }`,
              boxShadow:
                customShadows?.card || "0 20px 40px rgba(0,0,0,0.4)",
              background: "rgba(13, 20, 52, 0.85)",
              backdropFilter: "blur(12px)",
            }}
          >
            {/* 404 Large Display */}
            <Typography
              component="h1"
              variant="h1"
              sx={{
                fontSize: { xs: "5rem", sm: "7rem", md: "8.5rem" },
                fontWeight: 900,
                letterSpacing: "-0.04em",
                lineHeight: 1,
                background:
                  "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1.5,
                userSelect: "none",
              }}
            >
              404
            </Typography>

            {/* Title */}
            <Typography
              component="h2"
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.4rem", sm: "1.75rem" },
                color: "#ffffff",
                mb: 1.5,
              }}
            >
              Page Not Found
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: colors?.slate?.[400] || "rgba(255, 255, 255, 0.65)",
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                lineHeight: 1.6,
                maxWidth: 420,
                mx: "auto",
                mb: 4,
              }}
            >
              The page you are looking for doesn't exist, has been removed, or the
              URL might be mistyped.
            </Typography>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              alignItems="stretch"
            >
              <CommonButton
                variant="primary"
                size="large"
                startIcon={<Home size={18} />}
                onClick={() => history.push(Path.root)}
                label="Go to Homepage"
                sx={{
                  py: 1.35,
                  px: 3,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderRadius: borderRadiusPx?.lg || 2,
                }}
              />

              <CommonButton
                variant="outlined"
                size="large"
                startIcon={<Compass size={18} />}
                onClick={() => history.push(Path.allLotteries)}
                label="Explore Games"
                sx={{
                  py: 1.35,
                  px: 3,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderRadius: borderRadiusPx?.lg || 2,
                }}
              />
            </Stack>

            {/* Secondary navigation links */}
            <Stack
              direction="row"
              spacing={3}
              justifyContent="center"
              alignItems="center"
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() =>
                  history.length > 1 ? history.goBack() : history.push(Path.root)
                }
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: colors?.slate?.[400] || "rgba(255, 255, 255, 0.6)",
                  fontSize: "0.88rem",
                  fontWeight: 500,
                  padding: 0,
                }}
              >
                <ArrowLeft size={16} />
                <span>Go Back</span>
              </Box>

              <Box
                component={Link}
                to={Path.help}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: colors?.primary?.main || "#3b82f6",
                  fontSize: "0.88rem",
                  fontWeight: 500,
                  textDecoration: "none",
                }}
              >
                <HelpCircle size={16} />
                <span>Need Help?</span>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
